import "./reservationlist.css";
import { useState, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import API from "src/API2";

/* ── Inline icons ───────────────────────────────────────────────── */
const Svg = ({ children, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const IcSearch  = () => <Svg><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></Svg>;
const IcClose   = () => <Svg size={18}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></Svg>;
const IcMail    = () => <Svg size={14}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></Svg>;
const IcPhone   = () => <Svg size={14}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" /></Svg>;
const IcUser    = () => <Svg size={14}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></Svg>;
const IcUsers   = () => <Svg size={14}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></Svg>;
const IcBed     = () => <Svg size={14}><path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8" /><path d="M2 17h20" /><path d="M6 10V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v4" /></Svg>;
const IcCalIn   = () => <Svg size={14}><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" /></Svg>;
const IcCalOut  = () => <Svg size={14}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></Svg>;
const IcCash    = () => <Svg size={14}><rect x="2" y="6" width="20" height="12" rx="2" /><circle cx="12" cy="12" r="3" /><path d="M6 12h.01M18 12h.01" /></Svg>;
const IcCheck   = () => <Svg><polyline points="20 6 9 17 4 12" /></Svg>;
const IcX       = () => <Svg><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></Svg>;
const IcTrash   = () => <Svg><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></Svg>;
const IcEye     = () => <Svg size={14}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></Svg>;
const IcInbox   = () => <Svg><polyline points="22 12 16 12 14 15 10 15 8 12 2 12" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></Svg>;

/* ── Helpers ────────────────────────────────────────────────────── */
const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
};
const fmtMoney = (n) => {
  const v = Math.round(Number(n) || 0);
  return String(v).replace(/\B(?=(\d{3})+(?!\d))/g, " ") + " DA";
};
const nightsBetween = (a, b) => {
  if (!a || !b) return 0;
  const start = new Date(a); start.setHours(0, 0, 0, 0);
  const end = new Date(b); end.setHours(0, 0, 0, 0);
  const diff = (end - start) / 86400000;
  return Number.isFinite(diff) && diff > 0 ? Math.round(diff) : 0;
};
const initialsOf = (a, b) =>
  (((a || "?")[0] || "") + ((b || "")[0] || "")).toUpperCase();

/* Backend ReservationStatus enum: PENDING, CONFIRMED, REJECTED */
const STATUS_LABELS = {
  PENDING: "Pending",
  CONFIRMED: "Confirmed",
  REJECTED: "Rejected",
};

const isInHouse = (r) => {
  if (String(r.status).toUpperCase() !== "CONFIRMED") return false;
  const ci = new Date(r.checkin_date); ci.setHours(0, 0, 0, 0);
  const co = new Date(r.checkout_date); co.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return ci <= today && today < co;
};

/* ── Detail modal ───────────────────────────────────────────────── */
const InfoModal = ({ info, setActiveModal }) => {
  const u = info.user?.[0] || {};
  const status = String(info.status || "").toUpperCase();
  const nights = nightsBetween(info.checkin_date, info.checkout_date);
  const canConfirm = status === "PENDING";

  const handleConfirm = () => {
    toast.promise(
      API.get("reservations/expired/" + info.id).then(() => {
        setTimeout(() => window.location.reload(), 1500);
      }),
      {
        loading: "Confirming reservation…",
        success: "Reservation confirmed",
        error: "Could not confirm reservation",
      },
    );
  };

  const handleReject = () => {
    if (!window.confirm("Reject this reservation?")) return;
    toast.promise(
      API.get("reservations/reject/" + info.id).then(() => {
        setTimeout(() => window.location.reload(), 1200);
      }),
      {
        loading: "Rejecting reservation…",
        success: "Reservation rejected",
        error: "Could not reject reservation",
      },
    );
  };

  const handleRemove = () => {
    API.get("reservations/delete/" + info.id)
      .then(() => {
        toast.success("Reservation removed");
        setTimeout(() => window.location.reload(), 1200);
      })
      .catch(() => toast.error("Failed to remove reservation"));
  };

  return (
    <div className="rsv-modal-overlay" onClick={() => setActiveModal(false)}>
      <div className="rsv-modal rsv-modal--wide" onClick={(e) => e.stopPropagation()}>
        <header className="rsv-modal__head">
          <div className="rsv-modal__head-left">
            <span className="rsv-avatar rsv-avatar--xl">
              {initialsOf(u.firstname, u.lastname)}
            </span>
            <div>
              <span className="rsv-modal__eyebrow">Reservation #{String(info.id || "").slice(0, 8)}</span>
              <h3>{u.firstname || "—"} {u.lastname || ""}</h3>
              <span className={`rsv-status rsv-status--${status.toLowerCase()}`}>
                {STATUS_LABELS[status] || status}
              </span>
            </div>
          </div>
          <button className="rsv-icon-btn" onClick={() => setActiveModal(false)} aria-label="Close">
            <IcClose />
          </button>
        </header>

        <div className="rsv-modal__body">
          <section className="rsv-detail-section">
            <h4>Guest</h4>
            <div className="rsv-detail-grid">
              <div className="rsv-detail-row"><IcUser /><span>User ID</span><strong>{u.id ?? "—"}</strong></div>
              <div className="rsv-detail-row"><IcMail /><span>Email</span><strong>{u.email || "—"}</strong></div>
              <div className="rsv-detail-row"><IcPhone /><span>Phone</span><strong>{u.phone || "—"}</strong></div>
            </div>
          </section>

          <section className="rsv-detail-section">
            <h4>Stay</h4>
            <div className="rsv-detail-grid">
              <div className="rsv-detail-row"><IcCalIn /><span>Check-in</span><strong>{fmtDate(info.checkin_date)}</strong></div>
              <div className="rsv-detail-row"><IcCalOut /><span>Check-out</span><strong>{fmtDate(info.checkout_date)}</strong></div>
              <div className="rsv-detail-row"><IcUsers /><span>Adults · Children</span><strong>{info.nb_adults ?? 0} · {info.nb_children ?? 0}</strong></div>
              <div className="rsv-detail-row"><IcBed /><span>Room type</span><strong>{info.room?.description || "—"}</strong></div>
              <div className="rsv-detail-row"><IcInbox /><span>Rate</span><strong>{info.rate?.code || "—"}</strong></div>
              <div className="rsv-detail-row"><IcCash /><span>Total · {nights} night(s)</span><strong>{fmtMoney(info.price)}</strong></div>
            </div>
          </section>

          {info.special_demands && (
            <section className="rsv-detail-section">
              <h4>Special demands</h4>
              <p className="rsv-modal__text">{info.special_demands}</p>
            </section>
          )}
        </div>

        <footer className="rsv-modal__foot">
          <button
            type="button"
            className="rsv-btn rsv-btn--danger"
            title="Double-click to remove"
            onDoubleClick={handleRemove}
          >
            <IcTrash /> Remove
          </button>
          <button type="button" className="rsv-btn rsv-btn--ghost" onClick={() => setActiveModal(false)}>
            Close
          </button>
          {canConfirm && (
            <>
              <button type="button" className="rsv-btn rsv-btn--danger-soft" onClick={handleReject}>
                <IcX /> Reject
              </button>
              <button type="button" className="rsv-btn rsv-btn--primary" onClick={handleConfirm}>
                <IcCheck /> Confirm reservation
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
};

/* ── Main list ──────────────────────────────────────────────────── */
const ReservationsList = () => {
  const [reservations, setReservations] = useState([]);
  const [info, setInfo] = useState(null);
  const [activeModel, setActiveModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    API.get("reservations")
      .then((resp) => setReservations(resp?.data?.reservations || []))
      .catch(() => toast.error("Could not fetch reservations"))
      .finally(() => setLoading(false));
  }, []);

  /* Status mutations (backend):
     GET reservations/expired/:id → PENDING → CONFIRMED ("guest arrived")
     GET reservations/reject/:id  → PENDING → REJECTED  ("no-show / declined")
     GET reservations/delete/:id  → hard delete                                */
  const confirmRow = (r) => {
    if (!window.confirm(`Confirm reservation for ${r.user?.[0]?.firstname ?? ""} ${r.user?.[0]?.lastname ?? ""}?`)) return;
    toast.promise(
      API.get("reservations/expired/" + r.id).then(() => {
        setReservations((prev) =>
          prev.map((x) => (x.id === r.id ? { ...x, status: "CONFIRMED" } : x)),
        );
      }),
      {
        loading: "Confirming reservation…",
        success: "Reservation confirmed",
        error: "Could not confirm reservation",
      },
    );
  };

  const rejectRow = (r) => {
    if (!window.confirm(`Reject reservation for ${r.user?.[0]?.firstname ?? ""} ${r.user?.[0]?.lastname ?? ""}?`)) return;
    toast.promise(
      API.get("reservations/reject/" + r.id).then(() => {
        setReservations((prev) =>
          prev.map((x) => (x.id === r.id ? { ...x, status: "REJECTED" } : x)),
        );
      }),
      {
        loading: "Rejecting reservation…",
        success: "Reservation rejected",
        error: "Could not reject reservation",
      },
    );
  };

  const removeRow = (r) => {
    if (!window.confirm(`Delete reservation ${String(r.id || "").slice(0, 8)}? This cannot be undone.`)) return;
    toast.promise(
      API.get("reservations/delete/" + r.id).then(() => {
        setReservations((prev) => prev.filter((x) => x.id !== r.id));
      }),
      {
        loading: "Removing reservation…",
        success: "Reservation removed",
        error: "Could not remove reservation",
      },
    );
  };

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return reservations.filter((r) => {
      const u = r.user?.[0] || {};
      const fullName = `${u.lastname ?? ""} ${u.firstname ?? ""}`.toLowerCase();
      const phone = u.phone ?? "";
      const email = (u.email ?? "").toLowerCase();
      const status = String(r.status || "").toUpperCase();

      const matchesQ =
        !q ||
        fullName.includes(q) ||
        phone.includes(q) ||
        email.includes(q) ||
        status.toLowerCase().includes(q);

      let matchesStatus = true;
      if (statusFilter === "IN_HOUSE") matchesStatus = isInHouse(r);
      else if (statusFilter !== "ALL") matchesStatus = status === statusFilter;

      return matchesQ && matchesStatus;
    });
  }, [reservations, searchQuery, statusFilter]);

  const counts = useMemo(() => {
    const acc = {
      ALL: reservations.length,
      PENDING: 0,
      CONFIRMED: 0,
      REJECTED: 0,
      IN_HOUSE: 0,
    };
    reservations.forEach((r) => {
      const s = String(r.status || "").toUpperCase();
      if (acc[s] !== undefined) acc[s] += 1;
      if (isInHouse(r)) acc.IN_HOUSE += 1;
    });
    return acc;
  }, [reservations]);

  const FILTERS = [
    { key: "ALL", label: "All" },
    { key: "PENDING", label: "Pending" },
    { key: "CONFIRMED", label: "Confirmed" },
    { key: "IN_HOUSE", label: "In-house" },
    { key: "REJECTED", label: "Rejected" },
  ];

  return (
    <div className="rsv-list">
      {activeModel && info && <InfoModal setActiveModal={setActiveModal} info={info} />}

      <div className="rsv-toolbar">
        <div className="rsv-search">
          <IcSearch />
          <input
            type="text"
            placeholder="Search by name, phone, email, or status…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="rsv-search__clear" onClick={() => setSearchQuery("")} aria-label="Clear">
              <IcClose />
            </button>
          )}
        </div>
        <div className="rsv-filter-pills">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`rsv-pill ${statusFilter === f.key ? "is-active" : ""} rsv-pill--${f.key.toLowerCase()}`}
              onClick={() => setStatusFilter(f.key)}
            >
              {f.label}
              <span className="rsv-pill__count">{counts[f.key] ?? 0}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="rsv-card">
        <div className="rsv-table-wrap">
          <table className="rsv-table">
            <thead>
              <tr>
                <th className="rsv-table__no">#</th>
                <th>Guest</th>
                <th>Contact</th>
                <th>Stay</th>
                <th>Room</th>
                <th>Total</th>
                <th>Status</th>
                <th className="rsv-table__actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="rsv-table__loading">Loading reservations…</td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan="8">
                    <div className="rsv-empty">
                      <IcInbox />
                      <span>{reservations.length ? "No reservations match your filters." : "No reservations yet."}</span>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((r, i) => {
                  const u = r.user?.[0] || {};
                  const status = String(r.status || "").toUpperCase();
                  const nights = nightsBetween(r.checkin_date, r.checkout_date);
                  return (
                    <tr
                      key={r.id || i}
                      className="rsv-row"
                      onClick={() => { setInfo(r); setActiveModal(true); }}
                    >
                      <td className="rsv-table__no">{i + 1}</td>
                      <td>
                        <div className="rsv-guest">
                          <span className="rsv-avatar">{initialsOf(u.firstname, u.lastname)}</span>
                          <div className="rsv-guest__info">
                            <strong>{u.firstname || "—"} {u.lastname || ""}</strong>
                            <span>{(u.id ?? "—") + ""}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="rsv-stack">
                          <span className="rsv-line"><IcMail />{u.email || "—"}</span>
                          <span className="rsv-line rsv-line--muted"><IcPhone />{u.phone || "—"}</span>
                        </div>
                      </td>
                      <td>
                        <div className="rsv-stack">
                          <span className="rsv-line"><IcCalIn />{fmtDate(r.checkin_date)}</span>
                          <span className="rsv-line rsv-line--muted"><IcCalOut />{fmtDate(r.checkout_date)} · {nights}n</span>
                        </div>
                      </td>
                      <td>
                        <div className="rsv-stack">
                          <span className="rsv-line"><IcBed />{r.room?.description?.slice(0, 28) || "—"}</span>
                          <span className="rsv-line rsv-line--muted">
                            <IcUsers />{r.nb_adults ?? 0} adt · {r.nb_children ?? 0} ch
                          </span>
                        </div>
                      </td>
                      <td className="rsv-total">{fmtMoney(r.price)}</td>
                      <td>
                        <span className={`rsv-status rsv-status--${status.toLowerCase()}`}>
                          {STATUS_LABELS[status] || status}
                        </span>
                      </td>
                      <td className="rsv-table__actions">
                        {status === "PENDING" && (
                          <>
                            <button
                              className="rsv-icon-btn rsv-icon-btn--success"
                              onClick={(e) => { e.stopPropagation(); confirmRow(r); }}
                              title="Confirm — guest arrived"
                            >
                              <IcCheck />
                            </button>
                            <button
                              className="rsv-icon-btn rsv-icon-btn--danger"
                              onClick={(e) => { e.stopPropagation(); rejectRow(r); }}
                              title="Reject reservation"
                            >
                              <IcX />
                            </button>
                          </>
                        )}
                        <button
                          className="rsv-icon-btn"
                          onClick={(e) => { e.stopPropagation(); setInfo(r); setActiveModal(true); }}
                          title="View details"
                        >
                          <IcEye />
                        </button>
                        <button
                          className="rsv-icon-btn rsv-icon-btn--danger"
                          onClick={(e) => { e.stopPropagation(); removeRow(r); }}
                          title="Remove reservation"
                        >
                          <IcTrash />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {filtered.length > 0 && (
          <p className="rsv-table__footnote">
            Showing {filtered.length} of {reservations.length} reservations · Click a row to see full details.
          </p>
        )}
      </div>
    </div>
  );
};

export default ReservationsList;
